import { interval, EMPTY } from 'rxjs'
import { switchMap, map, filter, distinctUntilChanged, takeUntil } from 'rxjs/operators'
import { Epic, ofType, StateObservable } from 'redux-observable'
import { startOfDay } from 'date-fns'
import DashboardActionTypes from '../../actions/dashboard/DashboardActionTypes'
import { dayTransitionDetected, dayTransitionRefresh } from '../../actions/dashboard/DayTransition'
import { setIsLogged } from '../../actions/auth/SetIsLogged'
import { requestDashboard } from '../../actions/dashboard/RequestDashboard'
import { requestAvailableDays } from '../../actions/dashboard/RequestAvailableDays'
import { setSelectedDate } from '../../actions/dashboard/SetSelectedDate'
import { IStoreState } from '../../IStoreState'

// Store app start day for comparison
let appStartDay: string = startOfDay(new Date()).toISOString()

// Update app start day when day transition is handled
const updateAppStartDay = () => {
  appStartDay = startOfDay(new Date()).toISOString()
}

/**
 * Day Change Detection Epic
 * 
 * Monitors for day transitions (midnight) and triggers a complete refresh
 * when the day changes. This ensures all static date references are
 * recalculated and orders appear in the correct tabs.
 */
export const dayChangeDetectionEpic: Epic = (
  action$,
  state$: StateObservable<IStoreState>
) => {
  return action$.pipe(
    // Start monitoring after successful login
    ofType(DashboardActionTypes.REQUEST_SETTINGS_SUCCESS),
    switchMap(() =>
      // Check every 30 seconds for day changes
      interval(30 * 1000).pipe(
        map(() => {
          const currentDay = startOfDay(new Date()).toISOString()
          return { currentDay, appStartDay }
        }),
        // Only emit when day actually changes
        distinctUntilChanged(
          (prev, curr) => prev.currentDay === curr.currentDay
        ),
        filter(({ currentDay }) => currentDay !== appStartDay),
        map(({ currentDay }) => {
          console.log('[DayTransition] Day change detected!', {
            from: appStartDay,
            to: currentDay,
          })
          
          // Update stored app start day
          const previousDay = appStartDay
          updateAppStartDay()
          
          return dayTransitionDetected(previousDay, currentDay)
        }),
        // Stop monitoring when user logs out
        takeUntil(
          action$.pipe(
            filter(() => !state$.value.Auth.isLogged)
          )
        )
      )
    )
  )
}

/**
 * Day Transition Refresh Epic
 * 
 * Handles the actual refresh when day transition is detected.
 * Triggers a complete app-like restart by simulating logout/login
 * without actually requiring authentication again.
 */
export const dayTransitionRefreshEpic: Epic = (action$, state$: StateObservable<IStoreState>) => {
  return action$.pipe(
    ofType(DashboardActionTypes.DAY_TRANSITION_DETECTED),
    switchMap(() => {
      console.log('[DayTransition] Triggering complete app restart simulation...')
      
      // Simulate complete app restart sequence (like fresh login):
      // 1. Reset all dashboard state to initial values (clears stale availableDays)
      // 2. Set selected date to today (forces UI to sync to today's date)
      // 3. Trigger SET_IS_LOGGED to restart all initialization epics
      // 4. Explicitly request dashboard data for today
      // 5. Explicitly request available days (refreshes tab logic with fresh dates)
      // This ensures complete state reset like fresh app startup
      const todayDate = new Date()
      return [
        dayTransitionRefresh(),
        setSelectedDate({ date: todayDate }),
        setIsLogged(true),
        requestDashboard({
          date: todayDate,
          shouldResetOpenCycles: true,
        }),
        requestAvailableDays(),
      ]
    })
  )
}

/**
 * Day Transition Settings Update Epic
 * 
 * Resets app start day tracking when settings are refreshed
 * during day transition to prevent multiple triggers.
 */
export const dayTransitionSettingsUpdateEpic: Epic = (action$) => {
  return action$.pipe(
    ofType(DashboardActionTypes.DAY_TRANSITION_REFRESH),
    switchMap(() => {
      // Update the app start day to prevent immediate re-trigger
      updateAppStartDay()
      console.log('[DayTransition] App start day updated, refresh complete')
      return EMPTY
    })
  )
}

export const dayTransitionEpics = [
  dayChangeDetectionEpic,
  dayTransitionRefreshEpic,
  dayTransitionSettingsUpdateEpic,
]